import { ProductBadgeType } from '@services/MarppleShop/features/ProductList/type';
import { ProductBadgeBase } from '@services/MarppleShop/renderApp/components/cells/ProductBadge/ProductBadgeBase.ts';
import { ProductCardHelper } from '@services/MarppleShop/renderApp/components/cells/ProductCard/ProductCardHelper.ts';
import { ProductBadgeFactory } from '@services/MarppleShop/renderApp/components/cells/ProductBadge/ProductBadgeFactory.ts';
import { ProductBadgeHelper } from '@services/MarppleShop/renderApp/components/cells/ProductBadge/ProductBadgeHelper.ts';
import {
  ProductBadgeListData,
  ProductBadgeListOption,
  ProductBadgeListOverrideOption,
} from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/ProductBadgeList.ts';
import { filter, pipe, take, toArray } from '@fxts/core';

export class ProductBadgeListController {
  // 잔여 수량
  private readonly remaining_stock: number;

  private readonly limit: number;

  constructor(
    private readonly data: Readonly<ProductBadgeListData>,
    private readonly option: Readonly<ProductBadgeListOption>,
    private readonly override_option: Readonly<ProductBadgeListOverrideOption> = {},
  ) {
    this.remaining_stock = data.quantity - data.order_count;
    this.limit = option.max_length ?? Infinity;
  }

  /**
   * 뱃지 우선순위 (앞에서부터 보여짐, 즉 낮은 숫자일수록 높은 우선순위)
   */
  public static readonly BADGE_PRIORITY: ProductBadgeType[] = [
    ProductBadgeType.sold_out,
    ProductBadgeType.membership,
    ProductBadgeType.upcoming,
    ProductBadgeType.trending,
    ProductBadgeType.popular,
    ProductBadgeType.limited,
    ProductBadgeType.quantity_limited,
    ProductBadgeType.period_limited,
    ProductBadgeType.nft,
  ];

  /**
   * 상품 뱃지를 생성하여 반환하는 함수입니다.
   * - 개수 제한 없이 상품이 가질 수 있는 모든 뱃지를 반환하므로, `take(n)`을 하여 사용하도록 합니다.
   */
  public createProductBadges(): ProductBadgeBase[] {
    const badges: ProductBadgeBase[] = [];

    const mergeOption = (type: ProductBadgeType): ProductBadgeListOption => {
      return {
        ...this.option,
        ...this.override_option[type],
      };
    };

    // `Sold out` 품절
    // `Upcoming`인 경우에는 `Sold out`말고 `Upcoming` 뱃지를 노출한다.
    if (ProductCardHelper.isSoldOut(this.data) && !ProductCardHelper.isBeforeSale(this.data)) {
      badges.push(
        ProductBadgeFactory.create(ProductBadgeType.sold_out, {
          value: undefined,
          option: mergeOption(ProductBadgeType.sold_out),
        }),
      );
    }

    // `Membership` 뱃지, 주입한 경우에만 표시
    if (this.data.is_membership) {
      badges.push(
        ProductBadgeFactory.create(ProductBadgeType.membership, {
          value: undefined,
          option: mergeOption(ProductBadgeType.membership),
        }),
      );
    }

    // `Upcoming` 출시 예정
    if (ProductCardHelper.isBeforeSale(this.data)) {
      badges.push(
        ProductBadgeFactory.create(ProductBadgeType.upcoming, {
          value: this.data.sell_start_at,
          option: mergeOption(ProductBadgeType.upcoming),
        }),
      );
    }

    // 급상승
    if (ProductCardHelper.isTrending(this.data)) {
      badges.push(
        ProductBadgeFactory.create(ProductBadgeType.trending, {
          value: undefined,
          option: mergeOption(ProductBadgeType.trending),
        }),
      );
    }

    // 인기
    if (ProductCardHelper.isPopular(this.data)) {
      if (!ProductCardHelper.isTrending(this.data)) {
        badges.push(
          ProductBadgeFactory.create(ProductBadgeType.popular, {
            value: undefined,
            option: mergeOption(ProductBadgeType.popular),
          }),
        );
      }
    }

    if (ProductCardHelper.isLimited(this.data) && !ProductCardHelper.isBeforeSale(this.data)) {
      // `Limited` 기간 한정 OR 수량 한정
      badges.push(
        ProductBadgeFactory.create(ProductBadgeType.limited, {
          value: undefined,
          option: mergeOption(ProductBadgeType.limited),
        }),
      );

      // 수량한정, 기간한정 모두 보일 때
      if (ProductBadgeHelper.isBothQuantityAndPeriodLimitedBadgeVisible(this.data) && this.data.sell_end_at) {
        const period_limited_badge_priority = ProductBadgeHelper.getPeriodLimitedBadgePriority(
          this.data.sell_end_at,
        );
        const quantity_limited_badge_priority = ProductBadgeHelper.getQuantityLimitedBadgePriority(
          this.remaining_stock,
        );

        // 기간 한정뱃지가 수량한정보다 먼저 보여지는 조건
        if (period_limited_badge_priority < quantity_limited_badge_priority) {
          badges.push(
            ProductBadgeFactory.create(ProductBadgeType.period_limited, {
              value: this.data.sell_end_at,
              option: mergeOption(ProductBadgeType.period_limited),
            }),
          );
        } else {
          badges.push(
            ProductBadgeFactory.create(ProductBadgeType.quantity_limited, {
              value: this.remaining_stock,
              option: mergeOption(ProductBadgeType.quantity_limited),
            }),
          );
        }
      } else {
        // 수량한정 only
        if (ProductBadgeHelper.isQuantityLimitedBadgeVisible(this.data)) {
          badges.push(
            ProductBadgeFactory.create(ProductBadgeType.quantity_limited, {
              value: this.remaining_stock,
              option: mergeOption(ProductBadgeType.quantity_limited),
            }),
          );
        }
        // 기간한정 only
        else if (ProductBadgeHelper.isPeriodLimitedBadgeVisible(this.data) && this.data.sell_end_at) {
          badges.push(
            ProductBadgeFactory.create(ProductBadgeType.period_limited, {
              value: this.data.sell_end_at,
              option: mergeOption(ProductBadgeType.period_limited),
            }),
          );
        }
      }
    }

    // `NFT`
    if (this.data.is_token_gate) {
      badges.push(
        ProductBadgeFactory.create(ProductBadgeType.nft, {
          value: undefined,
          option: mergeOption(ProductBadgeType.nft),
        }),
      );
    }

    return pipe(
      badges,
      filter((badge) => badge.isVisible()),
      take(this.limit),
      toArray,
    );
  }
}
